import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { fmtZl } from '../lib/format';
import { orpc } from '../lib/orpc';
import { ACCENT_OPTIONS, useCreateApp, useShell, type Density } from '../shell';
import { Icon } from './Icon';

function useOutsideClose(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return ref;
}

function AppSwitcher() {
  const { apps, activeApp, setActiveAppId } = useShell();
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(() => setOpen(false));
  const queryClient = useQueryClient();

  const createApp = useCreateApp();

  const removeApp = useMutation(
    orpc.apps.remove.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: orpc.apps.key() }),
    }),
  );

  const addApp = () => {
    const name = window.prompt('Nazwa nowej aplikacji:');
    setOpen(false);
    if (name?.trim()) createApp.mutate({ name: name.trim() });
  };

  const deleteApp = (id: string, name: string) => {
    if (window.confirm(`Usunąć aplikację „${name}”? Wszystkie leady, research i aktywności zostaną trwale usunięte.`)) {
      removeApp.mutate({ id });
    }
  };

  return (
    <div className="appswitch" ref={ref}>
      <button className="appswitch-btn" onClick={() => setOpen((o) => !o)}>
        <span className="app-dot" />
        <span className="appswitch-name">{activeApp?.name ?? '—'}</span>
        <Icon name="chev" size={12} />
      </button>
      {open ? (
        <div className="appswitch-menu">
          <div className="menu-label">Aplikacje</div>
          {apps.map((a) => (
            <div className="menu-row" key={a.id}>
              <button
                className={`menu-item${a.id === activeApp?.id ? ' active' : ''}`}
                onClick={() => {
                  setActiveAppId(a.id);
                  setOpen(false);
                }}
              >
                <span className="app-dot" />
                {a.name}
                <span className="menu-count">{a.leadCount}</span>
              </button>
              <button
                className="menu-del"
                title="Usuń aplikację"
                disabled={removeApp.isPending}
                onClick={() => deleteApp(a.id, a.name)}
              >
                <Icon name="x" size={13} />
              </button>
            </div>
          ))}
          <div className="menu-sep" />
          <button className="menu-item add" onClick={addApp}>
            <Icon name="plus" size={13} /> Nowa aplikacja
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Preferences() {
  const { prefs, updatePrefs } = useShell();
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(() => setOpen(false));

  return (
    <div className="prefs" ref={ref}>
      <button className="icon-btn" title="Preferencje" onClick={() => setOpen((o) => !o)}>
        <Icon name="settings" size={15} />
      </button>
      {open ? (
        <div className="prefs-menu">
          <div className="prefs-row">
            <span className="menu-label" style={{ padding: 0 }}>
              Akcent
            </span>
            <div className="prefs-swatches">
              {ACCENT_OPTIONS.map((c) => (
                <button
                  key={c}
                  className="prefs-swatch"
                  data-on={prefs.accent === c ? '1' : '0'}
                  style={{ background: c }}
                  aria-label={c}
                  onClick={() => updatePrefs({ accent: c })}
                />
              ))}
            </div>
          </div>
          <div className="prefs-row">
            <span className="menu-label" style={{ padding: 0 }}>
              Gęstość tabel
            </span>
            <div className="seg">
              {(['kompaktowa', 'wygodna'] as Density[]).map((d) => (
                <button key={d} className={prefs.density === d ? 'on' : ''} onClick={() => updatePrefs({ density: d })}>
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="prefs-row prefs-row-h">
            <span className="menu-label" style={{ padding: 0 }}>
              Paski w tabelach
            </span>
            <button className="toggle" data-on={prefs.zebra ? '1' : '0'} onClick={() => updatePrefs({ zebra: !prefs.zebra })}>
              <i />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function Topbar({ title }: { title: string }) {
  const settings = useQuery(orpc.settings.get.queryOptions());
  const rate = settings.data?.hourlyRate;
  return (
    <header className="topbar">
      <AppSwitcher />
      <span className="topbar-sep">/</span>
      <span className="topbar-title">{title}</span>
      <div className="topbar-right">
        {rate != null ? <span className="chip muted-chip mono">{fmtZl(rate)}/h</span> : null}
        <Preferences />
      </div>
    </header>
  );
}
